var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector, Store } from '@ngxs/store';
import { EngineNodesStateModel } from './engine.nodes.state.model';
import { HoverNode, SelectNode, CheckNode, CheckInverseNodes, SetCheckedNodes, ToggleVisibleNodes, ExpandNode, CollapseNode, ResetEngineNodes, HideInverse, Isolate, CheckAllNodes, TogglePickableNodes, ToggleInversePickableNodes, QueryNodeInfo, QueryNodeInfoFailure, QueryNodeInfoSuccess, SetNodeInfo, PaintNodes, UnpaintNodes } from './engine.nodes.actions';
import { EngineState } from '../engine.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { EngineService } from '../../engine.service';
import { SetGraph, SynchronizePickableNodes } from '../engine.actions';
import { WikiService } from 'src/app/shared/wiki.service';
import { finalize } from 'rxjs/operators';
import { PaintService } from '../../paint.service';
const initialState = {
    hit: null,
    selectedNode: null,
    hoveredNode: null,
    checkedNodes: [],
    querying: false
};
let EngineNodesState = class EngineNodesState {
    constructor(store, graphService, engineService, wikiService, paintService) {
        this.store = store;
        this.graphService = graphService;
        this.engineService = engineService;
        this.wikiService = wikiService;
        this.paintService = paintService;
    }
    /**
     * Selectors
     */
    static getHit(state) {
        return state.hit;
    }
    static getHoveredNode(state) {
        return state.hoveredNode;
    }
    static getCheckedNodeIds(state) {
        return state.checkedNodes.map(node => node.id);
    }
    static getCheckedNodes(state) {
        return state.checkedNodes;
    }
    // TODO: is this used?
    static getSelectedNode(state) {
        return state.selectedNode;
    }
    static getQuerying(state) {
        return state.querying;
    }
    /**
     * Actions
     */
    // TODO: is this used?
    selectNode(context, action) {
        context.patchState({
            selectedNode: action.node
        });
    }
    hoverNode(context, action) {
        context.patchState({
            hoveredNode: action.node,
            hit: Object.assign({}, action.hit || null)
        });
    }
    checkNode(context, action) {
        const checkedNodes = context.getState().checkedNodes;
        if (action.checked) {
            // Add node
            const hasNode = checkedNodes.some((node) => node.id === action.node.id);
            if (!hasNode) {
                context.patchState({
                    checkedNodes: [...checkedNodes, action.node]
                });
            }
        }
        else {
            // Remove node
            const nodes = checkedNodes
                .filter((node) => node.id !== action.node.id);
            context.patchState({
                checkedNodes: [...nodes]
            });
        }
    }
    checkAllNodes(context, action) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        const nodes = Object.values(viewGraph.roots);
        const leaves = this.graphService.getAllLeafDescendants(viewGraph, nodes.map(node => node.id));
        const leafNodes = this.graphService.getNodes(viewGraph, leaves);
        // Update state with the new checked nodes
        context.patchState({
            checkedNodes: [...leafNodes]
        });
    }
    checkInverseNodes(context, action) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        // Get actual leaves from nodes passed in
        const leaves = this.graphService.getAllLeafDescendants(viewGraph, action.nodes.map(node => node.id));
        // Find the inversion of those leaves
        const inverseNodes = this.graphService.getInverseSelection(viewGraph, leaves);
        const nodes = this.graphService.getNodes(viewGraph, inverseNodes);
        // Update state with the new checked nodes
        context.patchState({
            checkedNodes: [...nodes]
        });
    }
    setCheckedNodes(context, action) {
        context.patchState({
            checkedNodes: [...action.nodes]
        });
    }
    toggleVisibleNodes(context, action) {
        const nodeIds = action.nodes.map(node => node.id);
        this.engineService.toggleNodes(nodeIds, action.visible);
    }
    togglePickableNodes(context, action) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        const nodeIds = action.nodes.map(node => node.id);
        const changeMap = this.engineService.setNodesPickable(viewGraph, nodeIds, action.pickable);
        this.store.dispatch(new SynchronizePickableNodes(changeMap));
    }
    toggleInversePickableNodes(context, action) {
        const nodeIds = action.nodes.map(node => node.id);
        const inverseNodeIds = this.graphService.getInverseSelection(action.viewGraph, nodeIds);
        const nodes = this.graphService.getNodes(action.viewGraph, inverseNodeIds);
        this.store.dispatch(new TogglePickableNodes(nodes, action.pickable));
    }
    expandNode(context, action) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        const ancestors = this.graphService.getAncestors(viewGraph, action.node.id, true);
        ancestors.forEach(id => {
            viewGraph.nodes[id].boolState.expanded = true;
        });
        this.store.dispatch(new SetGraph(viewGraph));
    }
    collapseNode(context, action) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        viewGraph.nodes[action.node.id].boolState.expanded = false;
        this.store.dispatch(new SetGraph(viewGraph));
    }
    resetEngineNodes(context, action) {
        context.setState(initialState);
    }
    hideInverse(context, action) {
        const nodeIds = action.nodes.map(node => node.id);
        const inverseNodeIds = this.graphService.getInverseSelection(action.viewGraph, nodeIds);
        const nodes = this.graphService.getNodes(action.viewGraph, inverseNodeIds);
        this.store.dispatch(new ToggleVisibleNodes(nodes, false));
    }
    isolate(context, action) {
        this.store.dispatch(new HideInverse(action.nodes, action.viewGraph));
        this.store.dispatch(new ToggleVisibleNodes(action.nodes, true, false));
    }
    queryNodeInfo(context, { node }) {
        // begin querying
        context.patchState({ querying: true });
        // query wiki service
        this.wikiService.getWikiText(node.label)
            .pipe(
        // end querying
        finalize(() => context.patchState({ querying: false })))
            .subscribe(
        // success
        (wikiDescription) => context.dispatch(new QueryNodeInfoSuccess(node, wikiDescription)), 
        // failure
        () => context.dispatch(new QueryNodeInfoFailure(node)));
    }
    queryNodeInfoSuccess(context, { node, description }) {
        context.dispatch(new SetNodeInfo(node, description));
    }
    setNodeInfo(context, { node, description }) {
        const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        const viewNode = viewGraph.nodes[node.id];
        if (viewNode) {
            viewNode.description = description;
            this.store.dispatch(new SetGraph(viewGraph));
            const selectedNode = context.getState().selectedNode;
            /*
            context.patchState({
              selectedNode: null
            });
            */
            context.patchState({
                selectedNode: selectedNode
            });
        }
    }
    paintNodes(context, { nodes, recipe }) {
        this.paintService.paintAll(nodes, recipe);
    }
    unpaintNodes(context, { nodes }) {
        nodes.forEach((node) => {
            this.paintService.unpaint(node);
        });
    }
};
__decorate([
    Action(SelectNode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SelectNode]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "selectNode", null);
__decorate([
    Action(HoverNode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, HoverNode]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "hoverNode", null);
__decorate([
    Action(CheckNode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CheckNode]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "checkNode", null);
__decorate([
    Action(CheckAllNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CheckAllNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "checkAllNodes", null);
__decorate([
    Action(CheckInverseNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CheckInverseNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "checkInverseNodes", null);
__decorate([
    Action(SetCheckedNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetCheckedNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "setCheckedNodes", null);
__decorate([
    Action(ToggleVisibleNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ToggleVisibleNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "toggleVisibleNodes", null);
__decorate([
    Action(TogglePickableNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, TogglePickableNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "togglePickableNodes", null);
__decorate([
    Action(ToggleInversePickableNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ToggleInversePickableNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "toggleInversePickableNodes", null);
__decorate([
    Action(ExpandNode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ExpandNode]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "expandNode", null);
__decorate([
    Action(CollapseNode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CollapseNode]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "collapseNode", null);
__decorate([
    Action(ResetEngineNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetEngineNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "resetEngineNodes", null);
__decorate([
    Action(HideInverse),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, HideInverse]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "hideInverse", null);
__decorate([
    Action(Isolate),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Isolate]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "isolate", null);
__decorate([
    Action(QueryNodeInfo),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, QueryNodeInfo]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "queryNodeInfo", null);
__decorate([
    Action(QueryNodeInfoSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, QueryNodeInfoSuccess]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "queryNodeInfoSuccess", null);
__decorate([
    Action(SetNodeInfo),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetNodeInfo]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "setNodeInfo", null);
__decorate([
    Action(PaintNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, PaintNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "paintNodes", null);
__decorate([
    Action(UnpaintNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, UnpaintNodes]),
    __metadata("design:returntype", void 0)
], EngineNodesState.prototype, "unpaintNodes", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getHit", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getHoveredNode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getCheckedNodeIds", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getCheckedNodes", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getSelectedNode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineNodesStateModel]),
    __metadata("design:returntype", void 0)
], EngineNodesState, "getQuerying", null);
EngineNodesState = __decorate([
    State({
        name: 'engineNodes',
        defaults: initialState
    }),
    __metadata("design:paramtypes", [Store,
        BdGraphService,
        EngineService,
        WikiService,
        PaintService])
], EngineNodesState);
export { EngineNodesState };
